import { Col, Row, Label, Input } from "reactstrap";
import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import AppShortcutOutlinedIcon from "@mui/icons-material/AppShortcutOutlined";

import {
  createShortcutAppData,
  createShortcutAppDataSuccess,
  getShortcutAppsList,
  updateShortcutAppData,
  updateShortcutAppDataSuccess,
} from "../../../store/shortcutApps/action";
import AppModal from "../../Common/AppModal";
import { faviconFromUrl } from "../../../helpers/functions/faviconFromUrl";
export default function CreateEditMyShortcuts({
  modal,
  setModal,
  toggle,
  isEdit,
  updateData,
}) {
  const dispatch = useDispatch();

  // Initial Values
  const defaultValues = {
    Title: "",
    LinkUrl: "",
    ImageUrl: "",
  };

  const [createEditData, setCreateEditData] = useState(defaultValues);

  // Task 24844 - "can we start by asking the link". The link is now the first
  // field, and once it is filled we derive a sensible Title and a favicon for
  // the icon from it, so most shortcuts need nothing else typed. Only fills
  // fields the user has left blank, so it never clobbers a manual entry (or the
  // existing values when editing).
  const deriveFromUrl = (rawUrl) => {
    if (!rawUrl) return { title: "", favicon: "" };
    try {
      const u = new URL(
        /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`
      );
      const host = u.hostname.replace(/^www\./i, "");
      const parts = host.split(".");
      // The main domain label: "app.activtrak.com" -> "activtrak",
      // "trello.com" -> "trello".
      const label = parts.length > 2 ? parts[parts.length - 2] : parts[0];
      const title = label
        ? label.charAt(0).toUpperCase() + label.slice(1)
        : "";
      return { title, favicon: faviconFromUrl(rawUrl) };
    } catch (e) {
      return { title: "", favicon: "" };
    }
  };

  const handleLinkUrlBlur = () => {
    setCreateEditData((prev) => {
      const { title, favicon } = deriveFromUrl(prev.LinkUrl);
      const next = { ...prev };
      if (!prev.Title && title) next.Title = title;
      if (!prev.ImageUrl && favicon) next.ImageUrl = favicon;
      return next;
    });
  };

  // Access Redux State
  const {
    validationResponseCreate,
    validationResponseUpdate,
    shortcutAppDataLoading,
  } = useSelector((state) => ({
    validationResponseCreate: state.ShortcutApps.createShortcutAppDataSuccess,
    validationResponseUpdate: state.ShortcutApps.updateShortcutAppDataSuccess,
    shortcutAppDataLoading: state.ShortcutApps.shortcutAppDataLoading,
  }));

  // Handle Create Product
  const handleCreateData = () => {
    dispatch(createShortcutAppData([createEditData]));
  };
  // Handle Update Product
  const handleUpdateData = () => {
    dispatch(updateShortcutAppData([createEditData]));
  };

  useEffect(() => {
    if (isEmpty(validationResponseCreate)) return;

    if (validationResponseCreate) {
      if (validationResponseCreate.status === 200) {
        toast.success(validationResponseCreate.message);

        // setModal(false);
        // clear form
        dispatch(getShortcutAppsList());
        setCreateEditData(defaultValues);
        setModal(false);
      } else {
        toast.error(validationResponseCreate.message);
      }
    }
    dispatch(createShortcutAppDataSuccess([]));
  }, [validationResponseCreate]);
  useEffect(() => {
    if (isEmpty(validationResponseUpdate)) return;

    if (validationResponseUpdate) {
      if (validationResponseUpdate.status === 200) {
        toast.success(validationResponseUpdate.message);

        // setModal(false);
        // clear form
        dispatch(getShortcutAppsList());
        setCreateEditData(defaultValues);
        setModal(false);
      } else {
        toast.error(validationResponseUpdate.message);
      }
    }
    dispatch(updateShortcutAppDataSuccess([]));
  }, [validationResponseUpdate]);

  // Get Edit Values and assign to state
  useEffect(() => {
    if (!isEdit) return;
    if (isEmpty(updateData)) return;
    setCreateEditData({
      Id: updateData?.Id,
      Title: updateData?.Title,
      LinkUrl: updateData?.LinkUrl,
      ImageUrl: updateData?.ImageUrl,
    });
  }, [isEdit, updateData]);

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title={isEdit ? "Edit Shortcut" : "Create Shortcut"}
      icon={<AppShortcutOutlinedIcon />}
      headerAccent="indigo"
      size="sm"
      loading={shortcutAppDataLoading}
      loadingText="Saving…"
      submitLabel={isEdit ? "Update" : "Create"}
      submitColor="success"
      onSubmit={() => {
        isEdit ? handleUpdateData() : handleCreateData();
      }}
    >
      <Row className="g-2">
            <>
              {/* Task 24844 - ask for the link first; Title + icon derive from it. */}
              <Col lg={12}>
                <Label title={"Link Url"}>Link Url</Label>
                <div>
                  <Input
                    type={"text"}
                    placeholder={"https://example.com"}
                    value={createEditData.LinkUrl}
                    onChange={(e) => {
                      setCreateEditData({
                        ...createEditData,
                        LinkUrl: e.target.value,
                      });
                    }}
                    onBlur={handleLinkUrlBlur}
                  />
                </div>
              </Col>

              <Col lg={12}>
                <div>
                  <Label>Title</Label>
                  <Input
                    type={"text"}
                    placeholder={"Title"}
                    value={createEditData.Title}
                    onChange={(e) => {
                      setCreateEditData({
                        ...createEditData,
                        Title: e.target.value,
                      });
                    }}
                  />
                </div>
              </Col>
              <Col lg={12}>
                <Label title={"Link Url Image"}>
                  Icon <span className="text-muted">(auto-filled from the link, or upload your own)</span>
                </Label>
                <div>
                  <Input
                    type="file"
                    className="form-control"
                    id="uploadFile"
                    onChange={(e) => {
                      // base64
                      const reader = new FileReader();
                      reader.readAsDataURL(e.target.files[0]);
                      reader.onload = () => {
                        setCreateEditData({
                          ...createEditData,
                          ImageUrl: reader.result,
                        });
                      };
                    }}
                  />
                </div>
                <div>
                  <a
                    href={createEditData?.LinkUrl || "/#"}
                    className="dropdown-icon-item"
                    // open new tab
                    target="_blank"
                    rel="noreferrer"
                  >
                    {createEditData?.ImageUrl ? (
                      <img
                        src={createEditData?.ImageUrl}
                        alt={createEditData?.Title}
                      />
                    ) : faviconFromUrl(createEditData?.LinkUrl) ? (
                      <img
                        src={faviconFromUrl(createEditData?.LinkUrl)}
                        alt={createEditData?.Title}
                      />
                    ) : (
                      <i className="fa-solid fa-link"></i>
                    )}

                    <span>{createEditData?.Title}</span>
                  </a>
                </div>
              </Col>

            </>
          </Row>
    </AppModal>
  );
}
